import React from 'react';
import { X } from 'src/components/common/icons';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';

interface ICheckMatchPickerModalProps {
    checkNo: string;
    matches: ILeaseCheck[];
    onSelect: (check: ILeaseCheck) => void;
    onClose: () => void;
}

const formatCurrency = (amount: number | null) =>
    amount !== null && amount !== undefined ? `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '-';

const formatDate = (dateString: string | null) => (dateString ? new Date(dateString).toLocaleDateString() : '-');

const companyLabel = (match: ILeaseCheck) => {
    if (match.company) {
        return match.company;
    }

    return match.compId !== null && match.compId !== undefined ? `Company ${match.compId}` : 'Unassigned';
};

const statusBadge = (match: ILeaseCheck) => {
    if (match.void === 'Y') {
        return <span style={styles.badgeVoid}>VOID</span>;
    }

    if (match.cleared === 'Y') {
        return <span style={styles.badgeCleared}>Cleared</span>;
    }

    return <span style={styles.badgePending}>Open</span>;
};

// The same check number is reused across companies, so a search can legitimately hit several
// rows. Show enough of each one -- company, date, amount, payee -- to tell them apart.
const CheckMatchPickerModal: React.FC<ICheckMatchPickerModalProps> = ({ checkNo, matches, onSelect, onClose }) => (
    <div style={styles.overlay} onClick={onClose}>
        <div style={styles.content} onClick={(e) => e.stopPropagation()}>
            <div style={styles.header}>
                <div>
                    <h3 style={styles.title}>Multiple Checks Found</h3>
                    <div style={styles.subtitle}>
                        {matches.length} checks share number #{checkNo}. Select the one you want.
                    </div>
                </div>
                <button onClick={onClose} style={styles.iconCloseButton} aria-label="Close">
                    <X style={styles.closeIcon} />
                </button>
            </div>

            <div style={styles.body}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Company</th>
                            <th style={styles.th}>Check Date</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                            <th style={styles.th}>Payee</th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th} />
                        </tr>
                    </thead>
                    <tbody>
                        {matches.map((match, index) => (
                            <tr key={match.checkId !== null ? match.checkId : index} style={styles.row} onClick={() => onSelect(match)}>
                                <td style={styles.td}>{companyLabel(match)}</td>
                                <td style={styles.td}>{formatDate(match.checkDate)}</td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(match.checkAmt)}</td>
                                <td style={{ ...styles.td, ...styles.payee }} title={match.payee || ''}>
                                    {match.payee || '-'}
                                </td>
                                <td style={styles.td}>{match.type || '-'}</td>
                                <td style={styles.td}>{statusBadge(match)}</td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>
                                    <button type="button" style={styles.selectButton}>
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        padding: '1.5rem',
        backdropFilter: 'blur(2px)',
    },
    content: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1.5rem 1.75rem',
        borderBottom: '1px solid #E5E7EB',
    },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#111827' },
    subtitle: { marginTop: '0.35rem', fontSize: '1rem', color: '#6B7280' },
    iconCloseButton: {
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: '0.25rem',
        lineHeight: 0,
        color: '#6B7280',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    body: { overflowY: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '1rem' },
    th: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        padding: '0.7rem 1.25rem',
        textAlign: 'left',
        backgroundColor: '#F9FAFB',
        color: '#6B7280',
        fontSize: '0.8125rem',
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        borderBottom: '1px solid #E5E7EB',
        whiteSpace: 'nowrap',
    },
    row: { cursor: 'pointer' },
    td: {
        padding: '0.85rem 1.25rem',
        color: '#111827',
        borderBottom: '1px solid #F3F4F6',
        verticalAlign: 'middle',
    },
    payee: { maxWidth: '380px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    selectButton: {
        padding: '0.4rem 0.95rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#F3F4F6',
        color: '#374151',
        fontSize: '0.9375rem',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    badgeVoid: {
        padding: '0.2rem 0.6rem',
        borderRadius: '999px',
        backgroundColor: '#FEE2E2',
        color: '#B91C1C',
        fontSize: '0.8125rem',
        fontWeight: 700,
    },
    badgeCleared: {
        padding: '0.2rem 0.6rem',
        borderRadius: '999px',
        backgroundColor: '#DCFCE7',
        color: '#15803D',
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    badgePending: {
        padding: '0.2rem 0.6rem',
        borderRadius: '999px',
        backgroundColor: '#F3F4F6',
        color: '#4B5563',
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
};

export default CheckMatchPickerModal;



// WEBPACK FOOTER //
// ./src/components/invoicing/components/CheckMatchPickerModal.tsx